/**
 * Stickers and GIFs.
 *
 * Stickers live in packs. A pack is `workspace` (curated by admins, listed for everyone) or `personal` (made by one
 * person, listed for them; anyone who meets one of its stickers in a message can open the pack and add it). Each
 * person keeps an ordered "My stickers" list of packs. Sticker images are files (files.ts): uploaded with
 * `CreateUploadRequest.purpose = "sticker"`, then attached by `CreateStickerRequest.fileId`; every workspace member can
 * view them (they are workspace content, like custom emoji). Stickers are 512×512 WebP made on the client, or an
 * animated WebP/APNG/GIF up to `MAX_STICKER_BYTES` (core `logic/stickers`).
 *
 * GIFs come from a provider (KLIPY). Per its terms the clients call it directly and hotlink its media; the API only
 * hands out the client key and the workspace's content filter (`GifConfigResponse`). A GIF message carries a `GifRef`.
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";

export const StickerPackScope = z.enum(["workspace", "personal"]);
export type StickerPackScope = z.infer<typeof StickerPackScope>;

export const Sticker = z.object({
  id: Id,
  packId: Id,
  name: z.string(),
  /** Lower-case search words (core `Stickers.normalizeKeywords`). */
  keywords: z.array(z.string()),
  /** The image file (files.ts): API-relative URL, media pipeline. */
  fileId: Id,
  url: z.string(),
  mime: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  animated: z.boolean().optional(),
  createdBy: Id,
  createdAt: Timestamp,
});
export type Sticker = z.infer<typeof Sticker>;

export const StickerPack = z.object({
  id: Id,
  name: z.string(),
  scope: StickerPackScope,
  createdBy: Id,
  createdAt: Timestamp,
  /** The sticker shown as the pack's icon (default: its first sticker). */
  iconStickerId: Id.optional(),
  /** In display order (oldest first). */
  stickers: z.array(Sticker),
});
export type StickerPack = z.infer<typeof StickerPack>;

/** Who may create stickers (full members; restricted members need `can.createStickers`). */
export const StickerCreators = z.enum(["admins", "everyone"]);
export type StickerCreators = z.infer<typeof StickerCreators>;

/** GIF content filter (KLIPY `content_filter`): "high" is the strictest. */
export const GifContentFilter = z.enum(["off", "low", "medium", "high"]);
export type GifContentFilter = z.infer<typeof GifContentFilter>;

/** Workspace settings for stickers and GIFs (admins change them). */
export const MediaSettings = z.object({
  stickerCreators: StickerCreators,
  gifContentFilter: GifContentFilter,
  /** GIF search on/off for the workspace (on by default when the server has a provider key). */
  gifsEnabled: z.boolean(),
});
export type MediaSettings = z.infer<typeof MediaSettings>;

/** Everything the sticker picker needs, for the caller. */
export const StickerLibraryResponse = z.object({
  /** Workspace packs, my personal packs and the packs in my list, each once. */
  packs: z.array(StickerPack),
  /** "My stickers": pack ids in my order. */
  myPackIds: z.array(Id),
  settings: MediaSettings,
  /** I may create stickers and packs (core `Stickers.canCreateStickers`). */
  canCreate: z.boolean(),
  /** I may curate workspace packs and change the settings (owners and admins). */
  canManage: z.boolean(),
});
export type StickerLibraryResponse = z.infer<typeof StickerLibraryResponse>;

export const StickerPackResponse = z.object({ pack: StickerPack });
export type StickerPackResponse = z.infer<typeof StickerPackResponse>;

export const CreateStickerPackRequest = z.object({
  name: z.string().min(1).max(50),
  /** "workspace" needs canManage. */
  scope: StickerPackScope,
  /** Retry-safe: the same clientId from the same person returns the same pack. */
  clientId: z.string().max(64).optional(),
});
export type CreateStickerPackRequest = z.infer<typeof CreateStickerPackRequest>;

/** Rename, change the icon, or (admins) move a pack between personal and workspace. */
export const UpdateStickerPackRequest = z.object({
  name: z.string().min(1).max(50).optional(),
  iconStickerId: Id.optional(),
  scope: StickerPackScope.optional(),
});
export type UpdateStickerPackRequest = z.infer<typeof UpdateStickerPackRequest>;

export const CreateStickerRequest = z.object({
  /** A ready upload of mine made with `purpose: "sticker"` (image, at most MAX_STICKER_BYTES). */
  fileId: Id,
  name: z.string().min(1).max(40),
  keywords: z.array(z.string().max(24)).max(10).optional(),
  animated: z.boolean().optional(),
  clientId: z.string().max(64).optional(),
});
export type CreateStickerRequest = z.infer<typeof CreateStickerRequest>;

export const StickerResponse = z.object({ sticker: Sticker });
export type StickerResponse = z.infer<typeof StickerResponse>;

/** Replaces "My stickers" (add, remove and reorder in one idempotent write). Unknown pack ids are dropped. */
export const UpdateMyStickersRequest = z.object({ packIds: z.array(Id).max(100) });
export type UpdateMyStickersRequest = z.infer<typeof UpdateMyStickersRequest>;

export const MyStickersResponse = z.object({ myPackIds: z.array(Id) });
export type MyStickersResponse = z.infer<typeof MyStickersResponse>;

export const UpdateMediaSettingsRequest = MediaSettings.partial();
export type UpdateMediaSettingsRequest = z.infer<typeof UpdateMediaSettingsRequest>;

export const MediaSettingsResponse = z.object({ settings: MediaSettings });
export type MediaSettingsResponse = z.infer<typeof MediaSettingsResponse>;

/**
 * How this client searches GIFs. `available: false` (no provider key on the server, or GIFs turned off for the
 * workspace) → the picker shows "GIFs unavailable". With KLIPY the client calls `api.klipy.com` itself with `apiKey`
 * (KLIPY's client-side partner key), `contentFilter` and `customerId`, hotlinks the media and sends the share ping.
 */
export const GifConfigResponse = z.object({
  available: z.boolean(),
  provider: z.enum(["klipy"]).optional(),
  apiKey: z.string().optional(),
  contentFilter: GifContentFilter,
  /** Stable per-person id for the provider (salted hash of the user id; no personal data). */
  customerId: z.string().optional(),
});
export type GifConfigResponse = z.infer<typeof GifConfigResponse>;

